import { Capacitor } from '@capacitor/core';

const LIDEA_ALBUM_NAME = 'Lidea';
const REVOKE_BLOB_URL_DELAY_MS = 60_000;

function isMobileUserAgent(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export function isMobileGalleryArchiveAvailable(): boolean {
  return Capacitor.isNativePlatform() || isMobileUserAgent();
}

export function isWebShareAvailableForFile(file: File): boolean {
  if (typeof navigator === 'undefined' || !navigator.share || !navigator.canShare) {
    return false;
  }

  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function ensureLideaAlbumIdentifier(): Promise<string | undefined> {
  const { Media } = await import('@capacitor-community/media');
  const { albums } = await Media.getAlbums();
  const existing = albums.find((album) => album.name === LIDEA_ALBUM_NAME);

  if (existing?.identifier) {
    return existing.identifier;
  }

  try {
    await Media.createAlbum({ name: LIDEA_ALBUM_NAME });
  } catch {
    // Album may already exist under a different identifier.
  }

  const refreshed = await Media.getAlbums();
  return refreshed.albums.find((album) => album.name === LIDEA_ALBUM_NAME)
    ?.identifier;
}

async function saveNativeToGallery(file: File): Promise<boolean> {
  try {
    const { Media } = await import('@capacitor-community/media');
    const dataUrl = await blobToDataUrl(file);
    const albumIdentifier = await ensureLideaAlbumIdentifier();
    const fileName = file.name.replace(/\.[^.]+$/, '') || 'lidea-crop';

    await Media.savePhoto({
      path: dataUrl,
      ...(albumIdentifier ? { albumIdentifier } : {}),
      fileName,
    });

    return true;
  } catch {
    try {
      const { Media } = await import('@capacitor-community/media');
      const dataUrl = await blobToDataUrl(file);
      await Media.savePhoto({ path: dataUrl });
      return true;
    } catch {
      return false;
    }
  }
}

function isUserShareCancellation(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function isMissingUserGesture(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'SecurityError')
  );
}

/**
 * Opens the OS share sheet so the user can save to Photos / Files on mobile PWA.
 */
export async function saveCropPictureViaWebShare(file: File): Promise<
  'shared' | 'cancelled' | 'unsupported' | 'blocked'
> {
  if (!isWebShareAvailableForFile(file)) {
    return 'unsupported';
  }

  try {
    await navigator.share({
      files: [file],
      title: file.name,
    });
    return 'shared';
  } catch (error) {
    if (isUserShareCancellation(error)) {
      return 'cancelled';
    }
    if (isMissingUserGesture(error)) {
      return 'blocked';
    }
    return 'blocked';
  }
}

/**
 * Best-effort download. Works on Android PWA when triggered from a user tap.
 */
export async function saveCropPictureViaDownload(file: File): Promise<boolean> {
  if (typeof document === 'undefined') {
    return false;
  }

  const url = URL.createObjectURL(file);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.name;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_BLOB_URL_DELAY_MS);
    return true;
  } catch {
    URL.revokeObjectURL(url);
    return false;
  }
}

/**
 * Saves a local copy of the crop picture.
 * Native apps write to the Lidea album; mobile PWA downloads to the device.
 */
export async function saveCropPictureToGallery(file: File): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    return saveNativeToGallery(file);
  }

  if (!isMobileUserAgent()) {
    return false;
  }

  return saveCropPictureViaDownload(file);
}

/**
 * Called from a button / action sheet so Android PWA keeps a valid user gesture.
 * Uses a silent download — no Web Share sheet.
 */
export async function saveCropPictureWithUserGesture(file: File): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    return saveNativeToGallery(file);
  }

  return saveCropPictureViaDownload(file);
}
